import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { districts } from "../data/locationData";
import "../styles/SuggestedLegalAdvisors.css";

const SuggestedLegalAdvisors = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { district, state, country } = location.state || {};
  const [legalAdvisors, setLegalAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Lets the user narrow state-level results down to a district on this page,
  // even if they didn't pick one earlier.
  const [refineDistrict, setRefineDistrict] = useState("");

  // The district actually used for filtering: the one passed in, or the one the
  // user picks here to refine a state-level search.
  const activeDistrict = district || refineDistrict;
  // Districts available for the refine dropdown (only when a state is known).
  const stateDistricts = state && districts[state] ? districts[state] : [];

  useEffect(() => {
    const fetchLegalAdvisors = async () => {
      setLoading(true);
      setError(null);
      try {
        // Progressive fallback so the user always gets useful results:
        //   district  → advisors in that district
        //   state/country (no district) → advisors across that state/country
        //   nothing selected → all verified advisors
        let response;
        if (activeDistrict) {
          response = await axios.get("/api/auth/legal-advisors-from-users-by-district", {
            params: { district: activeDistrict },
          });
        } else if (state || country) {
          response = await axios.get("/api/auth/legal-advisors-from-users-by-district", {
            params: { state, country },
          });
        } else {
          response = await axios.get("/api/auth/legal-advisors-from-users");
        }
        setLegalAdvisors(response.data);
      } catch (error) {
        console.error("Error fetching legal advisors:", error);
        setError("Failed to fetch legal advisors");
      } finally {
        setLoading(false);
      }
    };

    fetchLegalAdvisors();
  }, [activeDistrict, state, country]);

  const handleGoogleMapsSearch = () => {
    const queryParts = [
      "lawyer",
      "advocate",
      activeDistrict,
      state,
      country
    ].filter(Boolean);
    const mapsQuery = encodeURIComponent(queryParts.join(" "));
    window.open(`https://www.google.com/maps/search/${mapsQuery}`, "_blank");
  };

  if (loading) {
    return (
      <div className="suggested-advisors-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading legal advisors...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="suggested-advisors-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="suggested-advisors-page">
      <div className="suggested-advisors-container">
        <div className="header-section">
          <h1>Suggested Legal Advisors</h1>
          {activeDistrict ? (
            <p className="location-info">
              Based on your location: {[activeDistrict, state, country].filter(Boolean).join(", ")}
            </p>
          ) : (state || country) ? (
            <p className="location-info">
              Showing verified legal advisors across {[state, country].filter(Boolean).join(", ")}.
              Select a district below to narrow your results.
            </p>
          ) : (
            <p className="location-info">
              No location was selected, so we're showing all our verified legal advisors.
              Pick a state/district to see advisors near you.
            </p>
          )}

          {/* District refine: only when a district wasn't already chosen but a
              state (with known districts) is available. */}
          {!district && stateDistricts.length > 0 && (
            <div className="district-refine">
              <label htmlFor="refine-district">Choose your district in {state}:</label>
              <select
                id="refine-district"
                value={refineDistrict}
                onChange={(e) => setRefineDistrict(e.target.value)}
              >
                <option value="">All districts in {state}</option>
                {stateDistricts.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="google-maps-section">
          <button
            className="google-maps-btn"
            onClick={handleGoogleMapsSearch}
          >
            🔍 Find Legal Advisors on Google Maps
          </button>
        </div>

        <div className="advisors-section">
          {legalAdvisors.length > 0 ? (
            <div className="advisors-grid">
              {legalAdvisors.map((advisor) => (
                <div
                  key={advisor._id}
                  className="advisor-card"
                  onClick={() => navigate(`/legal-advisors/${advisor._id}`)}
                >
                  <div className="advisor-image-container">
                    <img
                      src={advisor.profilePhoto || "/default-profile.png"}
                      alt={`${advisor.firstName} ${advisor.lastName}`}
                      className="advisor-image"
                      onError={(e) => (e.target.src = "/default-profile.png")}
                    />
                  </div>
                  <div className="advisor-details">
                    <h3 className="notranslate" translate="no">Adv. {advisor.firstName} {advisor.lastName}</h3>
                    <p className="specialization">
                      <strong>Specialization:</strong> {advisor.specialization || "Not specified"}
                    </p>
                    <p className="district">
                      <strong>District:</strong> {advisor.district || "Not specified"}
                    </p>
                    {advisor.experience && (
                      <p className="experience">
                        <strong>Experience:</strong> {advisor.experience} years
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-advisors">
              <h3>No Legal Advisors Found</h3>
              <p>
                {activeDistrict
                  ? `No legal advisors found in ${activeDistrict}. Try a nearby district${stateDistricts.length ? " from the filter above" : ""} or use Google Maps to find legal professionals.`
                  : (state || country)
                  ? `No legal advisors found in ${[state, country].filter(Boolean).join(", ")}. Use Google Maps to find legal professionals near you.`
                  : "No legal advisors are listed yet. Use Google Maps to find legal professionals near you."}
              </p>
              <button
                className="google-maps-btn secondary"
                onClick={handleGoogleMapsSearch}
              >
                🔍 Search on Google Maps
              </button>
            </div>
          )}
        </div>

        <div className="back-section">
          <button onClick={() => navigate(-1)} className="back-btn">
            ← Go Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuggestedLegalAdvisors;